'use client';

import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import type { JobListingsBlock } from '../types';
import NavArrow from '@/assets/nav_arrow.svg';
import { SupportedLanguages } from '@/types/globals';
import { generateLink } from '@/components/utils/getLinkTarget';
import { supportedLanguages } from '@/configuration/languages';

export default function JobListingsBlock({
  block,
  locale = 'en',
}: {
  block: JobListingsBlock;
  locale?: SupportedLanguages;
}) {
  const router = useRouter();
  const careersIndexPath = `/${supportedLanguages[locale].careersBasePath}`;

  // Early return if block is not properly structured
  if (!block || typeof block !== 'object') {
    return null;
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 min-[1160px]:px-0">
      <div className="space-y-8">
        {/* Header Section - Centered */}
        {(block.pre_title || block.title) && (
          <div className="space-y-4 text-center">
            {block.pre_title && (
              <span
                className="font-source-sans-pro text-sm font-semibold tracking-[0.1em] text-indigo-800 uppercase"
                dangerouslySetInnerHTML={{ __html: block.pre_title }}
              />
            )}
            {block.title && (
              <h2 className="font-lora text-qf-black text-3xl font-medium md:text-4xl">
                <span dangerouslySetInnerHTML={{ __html: block.title }} />
              </h2>
            )}
          </div>
        )}

        {/* Job Listings - Left Aligned */}
        {block.jobs && Array.isArray(block.jobs) && block.jobs.length > 0 && (
          <div className="mx-auto max-w-[957px] space-y-4">
            {block.jobs.map((job, index) => {
              const descriptionText = job?.short_description;
              const jobTitle = job?.acf?.job_title || job?.title || 'Job Title';

              return (
                <div
                  key={job?.id || index}
                  className="group border-lilac-800 flex cursor-pointer items-center justify-between rounded-2xl border bg-indigo-50/70 px-[48px] py-[38px] transition-all duration-200 hover:shadow-md"
                  onClick={() => {
                    if (job?.slug) {
                      router.push(generateLink(locale, 'jobs', job.slug));
                    }
                  }}
                >
                  <div className="flex flex-col items-start gap-2">
                    <h3
                      className="font-inter text-center text-[17px] leading-6 font-normal text-[#53317F] capitalize"
                      dangerouslySetInnerHTML={{ __html: jobTitle }}
                    />
                    {descriptionText && (
                      <div
                        className="font-source-sans-pro text-graphite-700 line-clamp-2 text-base leading-6 font-normal"
                        dangerouslySetInnerHTML={{ __html: descriptionText }}
                      />
                    )}
                    {/* Department tags */}
                    {job?.acf?.department &&
                      Array.isArray(job.acf.department) &&
                      job.acf.department.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {job.acf.department.map(dept => (
                            <span
                              key={dept.id}
                              className="inline-flex items-center rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-medium text-indigo-800"
                            >
                              {dept.name}
                            </span>
                          ))}
                        </div>
                      )}
                  </div>
                  <div className="flex-shrink-0">
                    <NavArrow className="h-[14.54px] w-[14.54px] rotate-180 text-[#53317F] transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* See All Careers Button - Left Aligned */}
        {block.see_all_cta?.url && (
          <div className="pt-4">
            <Link
              href={block.see_all_cta.url || careersIndexPath}
              className="group font-source-sans-pro border-lilac-800 inline-flex items-center justify-center gap-2 rounded border px-5 py-3 text-sm font-semibold whitespace-nowrap text-indigo-800 transition-all duration-200 hover:border-indigo-800 hover:bg-indigo-800 hover:text-white"
            >
              <span
                dangerouslySetInnerHTML={{ __html: block.see_all_cta.text }}
              />
              <NavArrow className="h-4 w-4 rotate-180 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
